import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { 
  BsCheckCircleFill, 
  BsXCircleFill, 
  BsShieldCheck, 
  BsArrowRight,
  BsBagCheck, 
  BsHouseDoor,
  BsPrinter
} from "react-icons/bs";
import { PropagateLoader } from "react-spinners";

const PaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  // Razorpay Payment Link Query Parameters
  const paymentId = searchParams.get("razorpay_payment_id");
  const paymentLinkId = searchParams.get("razorpay_payment_link_id");
  const referenceId = searchParams.get("razorpay_payment_link_reference_id");
  const linkStatus = searchParams.get("razorpay_payment_link_status") || (paymentId ? "paid" : "failed");
  const signature = searchParams.get("razorpay_signature");

  const isSuccess = linkStatus.toLowerCase() === "paid" || Boolean(paymentId);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-4 bg-gray-50 px-4">
        <PropagateLoader color="#2563eb" />
        <p className="text-gray-600 text-sm font-medium mt-6 animate-pulse">
          Verifying payment with Razorpay...
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gradient-to-b from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        {/* TOP BANNER */}
        <div
          className={`p-8 text-center text-white ${
            isSuccess
              ? "bg-gradient-to-r from-emerald-600 to-teal-600"
              : "bg-gradient-to-r from-red-600 to-rose-600"
          }`}
        >
          <div className="flex justify-center mb-4">
            {isSuccess ? (
              <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                <BsCheckCircleFill className="w-16 h-16 text-white" />
              </div>
            ) : (
              <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm">
                <BsXCircleFill className="w-16 h-16 text-white" />
              </div>
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {isSuccess ? "Payment Successful!" : "Payment Failed or Cancelled"}
          </h1>
          <p className="mt-2 text-sm text-white/90">
            {isSuccess
              ? "Thank you! Your transaction has been processed securely."
              : "We could not verify the completion of your payment."}
          </p>
        </div>

        {/* PAYMENT SUMMARY DETAILS */}
        <div className="p-6 sm:p-8 space-y-6">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200/80 divide-y divide-gray-200">
            {paymentId && (
              <div className="py-2.5 flex justify-between items-center text-sm">
                <span className="text-gray-500 font-medium">Payment ID</span>
                <span className="font-mono text-gray-900 font-semibold select-all text-xs sm:text-sm">
                  {paymentId}
                </span>
              </div>
            )}

            {paymentLinkId && (
              <div className="py-2.5 flex justify-between items-center text-sm">
                <span className="text-gray-500 font-medium">Link ID</span>
                <span className="font-mono text-gray-900 font-semibold text-xs sm:text-sm">
                  {paymentLinkId}
                </span>
              </div>
            )}

            {referenceId && (
              <div className="py-2.5 flex justify-between items-center text-sm">
                <span className="text-gray-500 font-medium">Reference No</span>
                <span className="font-mono text-gray-900 font-semibold text-xs sm:text-sm">
                  {referenceId}
                </span>
              </div>
            )}

            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-500 font-medium">Status</span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  isSuccess
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {isSuccess ? "PAID" : linkStatus.toUpperCase()}
              </span>
            </div>

            <div className="py-2.5 flex justify-between items-center text-sm">
              <span className="text-gray-500 font-medium">Date & Time</span>
              <span className="text-gray-900 font-medium text-xs sm:text-sm">
                {new Date().toLocaleString("en-IN", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })}
              </span>
            </div>
          </div>

          {/* SECURITY BADGE */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
            <BsShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Verified 256-bit Encrypted Transaction by Razorpay</span>
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {isSuccess ? (
              <>
                <Link
                  to="/"
                  className="flex-1 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition shadow-sm"
                >
                  <BsHouseDoor className="w-4 h-4" /> Go to Home
                </Link>
                <Link
                  to="/shop"
                  className="flex-1 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition shadow-sm"
                >
                  <BsBagCheck className="w-4 h-4" /> Continue Shopping
                </Link>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="px-4 py-3 rounded-xl border border-gray-300 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition"
                  title="Print Receipt"
                >
                  <BsPrinter className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/"
                  className="flex-1 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 transition"
                >
                  <BsHouseDoor className="w-4 h-4" /> Go to Home
                </Link>
                <Link
                  to="/shop"
                  className="flex-1 inline-flex justify-center items-center gap-2 px-5 py-3 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition"
                >
                  Try Again <BsArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
